import React, { useEffect } from "react";

// Third Party
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ConfigProvider, theme as antdTheme } from "antd";

import AppRoutes from "./AppRoutes";
import DimeAiWidget from "./components/dimeai-widget/DimeAiWidget";

// Context
import { LoadingProvider } from "./context/common/CommonContext";
import { ThemeProvider, useTheme } from "./context/theme/ThemeContext";
import { ContentScaleProvider } from "./context/contentScale/ContentScaleContext";

// Store
import { RootState } from "./store/store";
import { getUserDetailsThunk } from "./store/usersSlice";
import { alertThunk } from "./store/alertsSlice";
import { AppDispatch } from "./store/store";

// Helpers
import { getLocalStorage } from "./helpers/utils";

// Hooks
import { useIdleTimer } from "./hooks";

import "./App.css";

// Dark-mode tokens for antd components (tables, modals, selects, dropdowns…)
// kept in sync with the layered "Slate" palette in App.css so antd surfaces
// share the exact same background hierarchy, borders and accent as the rest
// of the shell. Values mirror the [data-theme="dark"] CSS variables.
const darkAntdTheme = {
  algorithm: antdTheme.darkAlgorithm,
  token: {
    colorPrimary: "#05abdf",
    colorInfo: "#05abdf",
    colorSuccess: "#34d399",
    colorWarning: "#fbbf24",
    colorError: "#f87171",
    colorBgBase: "#0a0c11", // --app-bg
    colorBgContainer: "#161a23", // --surface-bg
    colorBgElevated: "#1d222e", // --surface-elevated
    colorBgLayout: "#0a0c11", // --app-bg
    colorBorder: "rgba(255, 255, 255, 0.10)", // --border-color
    colorBorderSecondary: "rgba(255, 255, 255, 0.06)", // --border-subtle
    colorText: "#e4e8f0", // --text-color
    colorTextSecondary: "#99a3b7", // --text-muted
    borderRadius: 8,
    boxShadowSecondary:
      "0 6px 20px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.35)",
  },
};

// Light mode is spelled out rather than passed as `undefined`. antd's
// ConfigProvider wraps its children in a DesignTokenContext.Provider only when
// `theme` is set, so alternating between an object and undefined changes the
// depth of the React tree — and React answers that by unmounting everything
// below and mounting it again from scratch. Every page lost its state on a
// theme toggle; DimeAI lost the conversation on screen, which read as the chat
// closing itself. `defaultAlgorithm` is what antd applies when no theme is
// given, so this keeps the tree stable without changing how anything looks.
const lightAntdTheme = { algorithm: antdTheme.defaultAlgorithm };

function AppContent() {
  const { loginDetails } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch<AppDispatch>();
  const { isDark } = useTheme();

  // Log the user out after 15 minutes of inactivity (frontend-only).
  useIdleTimer();

  const token = getLocalStorage("token");

  useEffect(() => {
    if (token || loginDetails?.access_token) {
      dispatch(getUserDetailsThunk());
      dispatch(alertThunk());
    }
  }, [dispatch, loginDetails]);

  return (
    <ConfigProvider theme={isDark ? darkAntdTheme : lightAntdTheme}>
      <div className="App">
        <LoadingProvider>
          <AppRoutes />
        </LoadingProvider>
        {/* Mounted as a sibling of <AppRoutes />, not inside it: routes here
            are flat siblings rather than nested under a shared layout route,
            so anything rendered from within the routed tree (MainLayout,
            PrivateRoute, a page) unmounts and remounts on every navigation.
            This is the one stable point in the tree that survives page
            changes for the whole session, which is what lets the floating
            chat keep its open/closed state and conversation while browsing
            between DimeRec, DimeXplain, etc. */}
        <DimeAiWidget />
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </ConfigProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ContentScaleProvider>
        <AppContent />
      </ContentScaleProvider>
    </ThemeProvider>
  );
}

export default App;
